import { useEffect, useMemo, useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Icon from '../../components/icon/Icon';
import Spinner from '../../components/Spinner/spinner';
import { getDashboardAvales } from '../../services/GuaranteesService';
import { formatMoney } from '../../utils/Masks';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { hasData } from '../../utils/Utils';
import { useConfig } from '../../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';

export type DashboardAvalesData = {
  fondo: {
    nominal: number;
    integrado: number;
    disponible: number;
  };
  mora: {
    total: number;
    contingente: number;
    totalArs: number;
    totalUsd: number;
    byInstrument: Array<{
      name: string;
      moraArs: number;
      moraUsd: number;
      pctPesos: number;
      pctDolares: number;
    }>;
    topDebtors: Array<{
      cuit: string;
      companyName: string;
      moraArs: number;
      moraUsd: number;
    }>;
  };
  operaciones: {
    riesgoVivo: number;
    limiteRiesgoVivo: number;
    pctFaltaLimite: number;
    gradoUtilizacion: number;
    apalancamiento: number;
    apalancamientoMaximo: number;
    instruments: Array<{
      name: string;
      quantityArs: number;
      quantityUsd: number;
      amountArs: number;
      amountUsd: number;
      fundPercentage: number;
    }>;
  };
  comisiones: {
    mesActual: { comisionMasGastos: number; referidos: number };
    mesAnterior: { comisionMasGastos: number; referidos: number };
    topReferidos: Array<{ name: string; totalAmount: number }>;
  };
};

const PIE_COLORS = ['#0F6E56', '#2D9CDB', '#F2994A', '#9B51E0', '#EB5757', '#27AE60'];

const formatUsd = (value: number | null | undefined): string => {
  if (value === null || value === undefined || value === 0) return '—';
  return `US$\u00A0${Math.abs(value).toLocaleString('es-AR')}`;
};

const formatPct = (value: number): string => `${value.toLocaleString('es-AR')}\u00A0%`;

const formatNumericValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return Math.round(Math.abs(value)).toLocaleString('es-AR');
};

const INSTRUMENT_ICON_MAP: Record<string, string> = {
  Cheques: 'document_outline',
  Pagarés: 'document_outline',
  Créditos: 'bank',
  'Operaciones Públicas': 'company',
  'Obligaciones Negociables': 'bank',
  'Avales Comerciales': 'instrumentation_outline',
};

const metricBoxBaseStyle = {
  minWidth: 0,
  background: '#F5F7FA',
  borderRadius: '8px',
  padding: '10px 10px',
  textAlign: 'left' as const,
  overflow: 'hidden',
};

const valueWrapStyle = {
  display: 'block',
  width: '100%',
  overflowWrap: 'anywhere' as const,
  wordBreak: 'break-word' as const,
  lineHeight: 1.15,
};

const MetricBox = ({
  label,
  value,
  kind = 'quantity',
}: {
  label: string;
  value: string;
  kind?: 'quantity' | 'amount';
}) => (
  <div
    style={{
      ...metricBoxBaseStyle,
      flex: kind === 'amount' ? 1.2 : 0.8,
    }}
  >
    <P type="4" variant="neutral" modifier="weak" withMargin={false}>
      {label}
    </P>
    <div style={valueWrapStyle}>
      <H
        type="2"
        align="left"
        weight="bold"
        modifier="strong"
        withMargin={false}
      >
        {value}
      </H>
    </div>
  </div>
);

const CurrencyMetricRow = ({
  currency,
  quantity,
  amount,
}: {
  currency: string;
  quantity: string;
  amount: string;
}) => (
  <div>
    <P type="4" variant="neutral" modifier="weak" withMargin={false}>
      {currency}
    </P>
    <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
      <MetricBox label="Cantidad" value={quantity} kind="quantity" />
      <MetricBox label="Monto" value={amount} kind="amount" />
    </div>
  </div>
);

const InstrumentCard = ({
  name,
  quantityArs,
  quantityUsd,
  amountArs,
  amountUsd,
  fundPercentage,
  localCurrencyCode,
}: {
  name: string;
  quantityArs: number;
  quantityUsd: number;
  amountArs: number;
  amountUsd: number;
  fundPercentage: number;
  localCurrencyCode: string;
}) => {
  const iconName = INSTRUMENT_ICON_MAP[name] ?? 'document_outline';
  const progressWidth = Math.min(Math.max(fundPercentage, 0), 100);

  return (
    <Card variant="neutral" modifier="regular" spacing="4" style={{ minWidth: 0 }}>
      <Card display="flex" alignItems="center" gap="8px" margin={{ bottom: '3' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name={iconName} variant="primary" height={22} width={22} />
        </div>
        <H type="3" weight="bold" modifier="strong" withMargin={false}>
          {name}
        </H>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <CurrencyMetricRow
          currency={localCurrencyCode}
          quantity={quantityArs.toLocaleString('es-AR')}
          amount={formatNumericValue(amountArs)}
        />
        <CurrencyMetricRow
          currency="USD"
          quantity={quantityUsd.toLocaleString('es-AR')}
          amount={formatNumericValue(amountUsd)}
        />

        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <P type="4" variant="neutral" modifier="weak" withMargin={false}>
              % del fondo consumido
            </P>
            <P type="3" variant="primary" weight="bold" withMargin={false}>
              {formatPct(fundPercentage)}
            </P>
          </div>
          <div
            style={{
              width: '100%',
              height: 8,
              borderRadius: 4,
              background: '#E8EBF0',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progressWidth}%`,
                height: '100%',
                borderRadius: 4,
                background: '#03BAA1',
              }}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

const KpiCard = ({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string;
  subtitle?: string;
}) => (
  <Card variant="neutral" modifier="regular" spacing="4">
    <Card display="flex" flexDirection="column" gap="8px">
      <P type="3" variant="neutral" modifier="weak" withMargin={false}>
        {title}
      </P>
      <div style={valueWrapStyle}>
        <H withMargin={false} variant="neutral-1" type="1" weight="bold" modifier="strong">
          {value}
        </H>
      </div>
      {subtitle && (
        <P type="4" variant="neutral" modifier="weak" withMargin={false}>
          {subtitle}
        </P>
      )}
    </Card>
  </Card>
);

const MoraTotalKpiCard = ({
  total,
  totalArs,
  totalUsd,
  localCurrencyCode,
}: {
  total: number;
  totalArs: number;
  totalUsd: number;
  localCurrencyCode: string;
}) => (
  <Card variant="neutral" modifier="regular" spacing="4">
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '16px',
        alignItems: 'flex-start',
      }}
    >
      <div>
        <P type="3" variant="neutral" modifier="weak" withMargin={false}>
          Mora total
        </P>
        <div style={valueWrapStyle}>
          <H
            withMargin={false}
            variant="neutral-1"
            type="1"
            weight="bold"
            modifier="strong"
          >
            {formatMoney(total)}
          </H>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          paddingTop: '4px',
        }}
      >
        <P type="1" variant="neutral" modifier="weak" withMargin={false}>
          {localCurrencyCode} {formatMoney(totalArs)}
        </P>
        <P type="1" variant="neutral" modifier="weak" withMargin={false}>
          Dólares {formatUsd(totalUsd)}
        </P>
      </div>
    </div>
  </Card>
);

const SectionTitle = ({ children }: { children: string }) => (
  <div style={{ marginBottom: '16px' }}>
    <H withMargin={false} type="2" weight="bold" modifier="strong">
      {children}
    </H>
  </div>
);

const SimpleTable = ({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | number)[][];
}) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {headers.map((header) => (
            <th
              key={header}
              style={{
                textAlign: 'left',
                padding: '12px 8px',
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 600,
              }}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            {row.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                style={{
                  padding: '10px 8px',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MoraPieChart = ({
  title,
  data,
}: {
  title: string;
  data: Array<{ name: string; value: number }>;
}) => (
  <Card variant="neutral" modifier="regular" spacing="4">
    <P type="3" variant="neutral" modifier="weak" withMargin={false}>
      {title}
    </P>
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
            {data.map((entry, index) => (
              <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => `${value}%`} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

const AvalesTab = () => {
  const fiscalId = useFiscalIdConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const [data, setData] = useState<DashboardAvalesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getDashboardAvales();
        if (hasData(response)) {
          setData(response.data);
        } else {
          setError('No se pudieron cargar los datos de Avales');
        }
      } catch {
        setError('Error al cargar los datos de Avales');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const moraGridRows = useMemo(() => {
    if (!data) return [];
    const instrumentRows = data.mora.byInstrument.map((item) => [
      item.name,
      formatMoney(item.moraArs),
      formatUsd(item.moraUsd),
      formatPct(item.pctPesos),
      formatPct(item.pctDolares),
    ]);
    return [
      ...instrumentRows,
      [
        'Total',
        formatMoney(data.mora.totalArs),
        formatUsd(data.mora.totalUsd),
        '100 %',
        '100 %',
      ],
    ];
  }, [data]);

  const piePesosData = useMemo(
    () =>
      (data?.mora.byInstrument ?? [])
        .filter((item) => item.pctPesos > 0)
        .map((item) => ({ name: item.name, value: item.pctPesos })),
    [data],
  );

  const pieDolaresData = useMemo(
    () =>
      (data?.mora.byInstrument ?? [])
        .filter((item) => item.pctDolares > 0)
        .map((item) => ({ name: item.name, value: item.pctDolares })),
    [data],
  );

  if (loading) {
    return (
      <Card display="flex" justifyContent="center" margin={{ top: '4' }}>
        <Spinner />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card margin={{ top: '4' }}>
        <P type="2" variant="danger" withMargin={false}>
          {error ?? 'Sin datos'}
        </P>
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', marginTop: '16px' }}>
      <section>
        <SectionTitle>Fondo</SectionTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
          }}
        >
          <KpiCard title="Fondo Nominal" value={formatMoney(data.fondo.nominal)} />
          <KpiCard title="Fondo Integrado" value={formatMoney(data.fondo.integrado)} />
          <KpiCard title="Fondo Disponible" value={formatMoney(data.fondo.disponible)} />
        </div>
      </section>

      <section>
        <SectionTitle>Mora</SectionTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginBottom: '16px',
          }}
        >
          <KpiCard title="Contingente" value={formatMoney(data.mora.contingente)} />
          <MoraTotalKpiCard
            total={data.mora.total}
            totalArs={data.mora.totalArs}
            totalUsd={data.mora.totalUsd}
            localCurrencyCode={localCurrencyCode}
          />
        </div>

        <Card variant="neutral" modifier="regular" spacing="4" margin={{ bottom: '4' }}>
          <div style={{ marginBottom: '8px' }}>
            <H withMargin={false} type="3" weight="bold" modifier="strong">
              Mora por instrumento
            </H>
          </div>
          <SimpleTable
            headers={[
              'Instrumento',
              `Mora ${localCurrencyCode}`,
              'Mora USD',
              '% Pesos',
              '% Dólares',
            ]}
            rows={moraGridRows}
          />
        </Card>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
            marginBottom: '16px',
          }}
        >
          <MoraPieChart title="% Pesos por instrumento" data={piePesosData} />
          <MoraPieChart title="% Dólares por instrumento" data={pieDolaresData} />
        </div>

        <Card variant="neutral" modifier="regular" spacing="4">
          <div style={{ marginBottom: '8px' }}>
            <H withMargin={false} type="3" weight="bold" modifier="strong">
              Top 10 clientes más morosos
            </H>
          </div>
          <SimpleTable
            headers={['Cliente', fiscalId.label, `Mora ${localCurrencyCode}`, 'Mora USD']}
            rows={data.mora.topDebtors.map((debtor) => [
              debtor.companyName,
              fiscalId.format(debtor.cuit),
              formatMoney(debtor.moraArs),
              formatUsd(debtor.moraUsd),
            ])}
          />
        </Card>
      </section>

      <section>
        <SectionTitle>Operaciones</SectionTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginBottom: '16px',
          }}
        >
          <KpiCard title="Riesgo vivo" value={formatMoney(data.operaciones.riesgoVivo)} />
          <KpiCard
            title="Límite de riesgo vivo"
            value={formatMoney(data.operaciones.limiteRiesgoVivo)}
            subtitle={`falta ${formatPct(data.operaciones.pctFaltaLimite)}`}
          />
          <KpiCard
            title="Grado de utilización"
            value={formatPct(data.operaciones.gradoUtilizacion)}
          />
          <KpiCard
            title="Apalancamiento"
            value={`${data.operaciones.apalancamiento.toLocaleString('es-AR')} x`}
            subtitle={`máx ${data.operaciones.apalancamientoMaximo.toLocaleString('es-AR')} x`}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
          }}
        >
          {data.operaciones.instruments.map((instrument) => (
            <InstrumentCard
              key={instrument.name}
              name={instrument.name}
              quantityArs={instrument.quantityArs}
              quantityUsd={instrument.quantityUsd}
              amountArs={instrument.amountArs}
              amountUsd={instrument.amountUsd}
              fundPercentage={instrument.fundPercentage}
              localCurrencyCode={localCurrencyCode}
            />
          ))}
        </div>
      </section>

      <section>
        <SectionTitle>Comisiones</SectionTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginBottom: '16px',
          }}
        >
          <KpiCard
            title="Comisiones mes actual"
            value={formatMoney(data.comisiones.mesActual.comisionMasGastos)}
            subtitle={`anterior ${formatMoney(data.comisiones.mesAnterior.comisionMasGastos)}`}
          />
          <KpiCard
            title="Comisiones a referidos mes actual"
            value={formatMoney(data.comisiones.mesActual.referidos)}
            subtitle={`anterior ${formatMoney(data.comisiones.mesAnterior.referidos)}`}
          />
        </div>

        <Card variant="neutral" modifier="regular" spacing="4">
          <div style={{ marginBottom: '8px' }}>
            <H withMargin={false} type="3" weight="bold" modifier="strong">
              Top 10 referidos por comisiones cobradas
            </H>
          </div>
          <SimpleTable
            headers={['Referido', 'Comisiones cobradas']}
            rows={data.comisiones.topReferidos.map((referrer) => [
              referrer.name,
              formatMoney(referrer.totalAmount),
            ])}
          />
        </Card>
      </section>
    </div>
  );
};

export default AvalesTab;
